import { useEffect } from 'react'
import queryString from 'query-string'

import { toggleArray } from '@domain-group/javascript-utils'
import { useLatestEntity, useSetState } from '@domain-group/react-hooks'

import { baseURL } from '@functions/api'

import useCampaignItemsAndOrders from '@hooks/useCampaignItemsAndOrders'
import useCampaignQuotes from '@hooks/useCampaignQuotes'
import useCurrentUser from '@hooks/useCurrentUser'

import type { CampaignModel, UserModel } from '@models/types'

type DefaultState = {
  selectedCampaignItemIds: number[],
  selectedOrderIds: number[],
  selectedPendingQuoteIds: number[],
}

const defaultState: DefaultState = {
  selectedCampaignItemIds: [],
  selectedOrderIds: [],
  selectedPendingQuoteIds: [],
}

type DownloadCampaignScheduleParams = {
  campaign: CampaignModel,
  currentUser: UserModel,
  state: Partial<typeof defaultState>,
}

const downloadCampaignSchedule = (params: DownloadCampaignScheduleParams) => {
  const {
    campaign,
    currentUser,
    state: {
      selectedCampaignItemIds,
      selectedOrderIds,
      selectedPendingQuoteIds,
    },
  } = params

  const queryParams = {
    campaign_item_ids: selectedCampaignItemIds,
    order_ids: selectedOrderIds,
    public_token: campaign.public_token,
    quote_ids: selectedPendingQuoteIds,
    user_id: currentUser.id,
  }

  const queryParamString = queryString.stringify(queryParams, { arrayFormat: 'comma' })
  const downloadLink = `${baseURL}/campaigns/${campaign.id}/campaign_schedule_pdf?${queryParamString}`

  window.open(downloadLink)
}

type SetState = (state: Partial<typeof defaultState>) => void

type SelectEntityIdParams = {
  key: keyof DefaultState,
  id: number,
  state: Partial<typeof defaultState>,
  setState: SetState,
}

const selectEntityId = (params: SelectEntityIdParams) => {
  const {
    key, id, state, setState,
  } = params

  setState({
    // @ts-expect-error TS2345
    [key]: toggleArray(state[key], id),
  })
}

type UseGenerateCampaignScheduleOptions = {
  campaign: CampaignModel,
}

function useGenerateCampaignSchedule(options: UseGenerateCampaignScheduleOptions) {
  const { campaign: initCampaign } = options

  const { entity: campaign } = useLatestEntity(initCampaign, 'campaigns')

  const [state, setState] = useSetState(defaultState)
  const { selectedCampaignItemIds, selectedOrderIds, selectedPendingQuoteIds } = state

  // Current User
  const { currentUser } = useCurrentUser()

  // Items & Orders
  const campaignItemsAndOrdersPayload = useCampaignItemsAndOrders({ campaign })
  const {
    filteredCampaignItems,
    groupedOrders,
  } = campaignItemsAndOrdersPayload

  // Autoselect Items
  useEffect(() => {
    setState({
      selectedCampaignItemIds: filteredCampaignItems.map(campaignItem => campaignItem.id),
      // @ts-expect-error TS2339
      selectedOrderIds: groupedOrders.visible.map(order => order.id),
    })
  }, [filteredCampaignItems.length, groupedOrders.visible.length])

  // Campaign Quotes
  const campaignQuotesPayload = useCampaignQuotes({
    filters: {
      campaign_id: campaign.id,
      status: 'pending',
    },
  })
  const { filteredCampaignQuotes } = campaignQuotesPayload

  const hasSelectedItems = selectedCampaignItemIds.length
  || selectedOrderIds.length
  || selectedPendingQuoteIds.length

  return {
    callbacks: {
      // @ts-expect-error TS2322
      downloadSchedule: () => downloadCampaignSchedule({ campaign, currentUser, state }),
      selectEntityId: (key: keyof DefaultState, id: number) => selectEntityId({
        key, id, state, setState,
      }),
    },
    campaign,
    filteredCampaignQuotes,
    filteredCampaignItems,
    hasSelectedItems,
    state,
    groupedOrders,
  }
}

export default useGenerateCampaignSchedule
